import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type UserListItem = {
  id: number;
  company_id: number;
  email: string;
  username: string;
  role_id: number;
  status_id: number;
  phone_number?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type PagedUsers = {
  total: number;
  page: number;
  page_size: number;
  items: UserListItem[];
};

export type ListUsersParams = {
  query?: string;
  role_id?: number;
  status_id?: number | '';
  created_by?: 'me';
  page?: number;
  page_size?: number;
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    // POST /v1/users (preferido) o /v1/register (legacy)
    createUser: builder.mutation<
      ApiResponseBase<{ id: number } | { user_id: number } | unknown>,
      {
        email: string;
        username: string;
        password: string;
        phone_number?: string | null;
        display_name?: string | null;
        role_id: number;
        status_id: number;
      }
    >({
      query: (body) => ({ url: '/v1/users', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Users.Create'),
      invalidatesTags: ['Users'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear usuario',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.Create'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.create',
              endpointName: e?.endpointName || 'Users.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/users/all — solo para rol root: lista todos los usuarios
    listUsersAll: builder.query<ApiResponseBase<PagedUsers>, Omit<ListUsersParams, 'created_by'>>({
      query: (p) => ({
        url: '/v1/users/all',
        method: 'GET',
        params: {
          query: p?.query ?? '',
          role_id: p?.role_id,
          status_id: p?.status_id,
          page: p?.page ?? 1,
          page_size: p?.page_size ?? 20,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Users.ListAll'),
      providesTags: ['Users'],
      keepUnusedDataFor: 60,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar todos los usuarios',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.ListAll'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.list.all',
              endpointName: e?.endpointName || 'Users.ListAll',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    listUsers: builder.query<ApiResponseBase<PagedUsers>, ListUsersParams>({
      query: (p) => ({
        url: '/v1/users',
        method: 'GET',
        params: {
          query: p?.query ?? '',
          role_id: p?.role_id,
          status_id: p?.status_id,
          created_by: p?.created_by,
          page: p?.page ?? 1,
          page_size: p?.page_size ?? 20,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Users.List'),
      providesTags: ['Users'],
      // Mantener datos en caché para evitar re-fetch por doble montaje en dev
      keepUnusedDataFor: 60,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar usuarios',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.List'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.list',
              endpointName: e?.endpointName || 'Users.List',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    updateUser: builder.mutation<
      ApiResponseBase<{ id: number } | { updated: number } | unknown>,
      {
        id: number | string;
        body: {
          email: string;
          username: string;
          phone_number?: string | null;
          display_name?: string | null;
          role_id: number;
          status_id: number;
        };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/users/${id}`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Users.Update'),
      invalidatesTags: ['Users'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar usuario',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.Update'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.update',
              endpointName: e?.endpointName || 'Users.Update',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/users/{id}/password
    updateUserPassword: builder.mutation<
      ApiResponseBase<{ id?: number } | unknown>,
      { id: number | string; body: { password: string } }
    >({
      query: ({ id, body }) => ({ url: `/v1/users/${id}/password`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Users.UpdatePassword'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar contraseña',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.UpdatePassword'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.update.password',
              endpointName: e?.endpointName || 'Users.UpdatePassword',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/users/me/password
    updateMyPassword: builder.mutation<
      ApiResponseBase<{ id?: number } | unknown>,
      { password: string }
    >({
      query: ({ password }) => ({
        url: `/v1/users/me/password`,
        method: 'PUT',
        body: { password },
      }),
      transformErrorResponse: handlerErrorResponse('Users.UpdateMyPassword'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar tu contraseña',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Users.UpdateMyPassword'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'users.update.me.password',
              endpointName: e?.endpointName || 'Users.UpdateMyPassword',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useListUsersQuery,
  useListUsersAllQuery,
  useLazyListUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserPasswordMutation,
  useUpdateMyPasswordMutation,
} = usersApi;
